import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  Controller,
  type FieldValues,
  type Path,
  type PathValue,
  useFormContext,
} from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { type ElementTracking, useElementTracking } from "../../../analytics/analytics";
import { CeFontDropdown, type Fonts } from "./ce-font-dropdown";

const customOption = "Custom Font";

export const CeFontCustomDropdown = <TFieldValues extends FieldValues>({
  name,
  fallbackName,
  label,
  fonts,
  analyticsCategory,
}: {
  name: Path<TFieldValues>;
  fallbackName: Path<TFieldValues>;
  label: string;
  fonts: Fonts;
  onChange?: (value: string) => void;
} & ElementTracking): React.JSX.Element => {
  const { t } = useTranslation();
  const { open: dialogOpen, handleOpen: openDialog, handleClose: closeDialog } = useDialog();

  const options = [customOption, ...fonts.webSafe.concat(fonts.web)];

  const { setValue: setFormValue, getValues, control } = useFormContext<TFieldValues>();

  const [value, setValue] = useState<string | null>(() => {
    const val = getValues(name);
    if (!val) return null;

    return options.includes(val) ? val : customOption;
  });

  const isCustom = useMemo(() => value === customOption, [value]);

  const analyticsProps = useElementTracking(analyticsCategory, name, "check");

  const groupBy = useCallback(
    (font: string): string => {
      if (font === customOption) return "";
      return fonts.webSafe.includes(font) ? t("Web-Safe Fonts") : t("Web Fonts");
    },
    [fonts, t],
  );

  const onChange = useCallback(
    (val: string | null) => {
      setValue(val);
      setFormValue(name, null as PathValue<TFieldValues, Path<TFieldValues>>);
      setFormValue(fallbackName, null as PathValue<TFieldValues, Path<TFieldValues>>);

      if (val === customOption) openDialog();
    },
    [name, fallbackName, setValue, setFormValue, openDialog],
  );

  useEffect(() => {
    if (value && !isCustom) {
      setFormValue(name, value as PathValue<TFieldValues, Path<TFieldValues>>);
    }
  }, [isCustom, name, setFormValue, value]);

  return (
    <>
      <Autocomplete
        value={value}
        onChange={(_, val) => onChange(val)}
        options={options}
        groupBy={fonts.web.length > 0 ? groupBy : undefined}
        renderOption={(params, option) => <Option key={option} params={params} option={option} />}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={label}
            fullWidth
            sx={{
              "& .MuiInputBase-input": {
                fontFamily: value && !isCustom ? value : "inherit",
                color: isCustom ? "primary.main" : "inherit",
              },
            }}
            {...analyticsProps}
          />
        )}
      />
      {isCustom && (
        <>
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("Custom Font")}
                placeholder={t("Custom Font")}
                fullWidth
                onChange={field.onChange}
                sx={{
                  "& .MuiInputBase-input": {
                    fontFamily: field.value ? field.value : "inherit",
                  },
                }}
                {...analyticsProps}
              />
            )}
          />
          <CeFontDropdown<TFieldValues>
            analyticsCategory={analyticsCategory}
            name={fallbackName}
            label={t("Fallback Font Family")}
            fonts={fonts}
          />
        </>
      )}
      <WarningModal open={dialogOpen} closeModal={closeDialog} />
    </>
  );
};

const Option: FC<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: React.HTMLAttributes<HTMLLIElement> & { key: any };
  option: string;
}> = ({ params, option }) => {
  if (option === customOption) {
    return (
      <Typography {...params} key={option} color="primary">
        {option}
      </Typography>
    );
  }

  return (
    <Typography {...params} sx={{ fontFamily: option }} key={option}>
      {option}
    </Typography>
  );
};

const WarningModal: FC<{ open: boolean; closeModal: () => void }> = ({ open, closeModal }) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={closeModal}>
      <DialogTitle>{t("Custom fonts")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Trans>
            <p>
              Enter the name of your custom font into the Custom font name field. The name must be
              entered exactly as it is named on your computer, otherwise it will not display
              correctly.
            </p>
          </Trans>
          <Trans>
            <p>
              To view custom fonts, all ChangeEngine editors, and your recipients, must have the
              font installed on their machines already. You must choose a Fallback font to display
              if they do not have the custom font installed.
            </p>
          </Trans>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          data-analytics-id="email-design-custom-font-dialog-continue"
          variant="outlined"
          onClick={closeModal}
        >
          {t("Continue")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
